import { CloseOutlined } from '@ant-design/icons';
import { Col, Modal, Row } from 'antd';
import Buttons from 'components/Global/Buttons';
import CheckBox from 'components/Global/Checkbox';
import InputField from 'components/Global/InputField';
import InputNumberField from 'components/Global/InputNumberField';
import TextareaFieldInput from 'components/Global/textareaField';
import { useEffect, useState } from 'react';
import {
	hasFormError,
	invalidText,
	toastText,
	validateFormData,
} from 'utils/utils';
import ConfigRuleModal from '../ConfigRuleModal';
import styles from './index.module.scss';
import './index.scss';
import { leaveManagementApi } from 'Api/masters/leave-management';

type AddLeaveManagementModalProps = {
	open: boolean;
	handleCancel: () => void;
	isEdit: boolean;
	editData: any;
	openConfigRuleModal: () => void;
	configRuleModal: boolean;
	fetchLeaveManagementData: () => void;
};

const AddLeaveManagementModal = (props: AddLeaveManagementModalProps) => {
	const {
		open,
		handleCancel,
		isEdit,
		editData,
		openConfigRuleModal,
		fetchLeaveManagementData,
		configRuleModal,
	} = props;

	const [isLoading, setIsLoading] = useState(false);
	const [leaveManagementId, setLeaveManagementId] = useState<any>(null);
	const [configLoading, setConfigLoading] = useState(false);
	const [leaveHistoryId, setLeaveHistoryId] = useState<any>(null);
	const [leaveName, setLeaveName] = useState<string | null>(null);
	const [EditForConfig, setEditForConfig] = useState<boolean>(false);
	const [addLeaveDetails, setAddLeaveDetails] = useState<any>({
		name: null,
		description: null,
		isAttachmentRequired: false,
		maxPaidLeavesPerMonth: null,
		leaveEncashment: false,
	});

	const [formError, setFormError] = useState<any>({
		name: false,
		description: false,
		maxPaidLeavesPerMonth: false,
	});

	const [formErrorMessages, setFormErrorMessages] = useState<any>({
		name: '',
		description: '',
		maxPaidLeavesPerMonth: '',
	});

	//FOR CANCEL BUTTON ONLY
	const handleModalCancel = () => {
		setAddLeaveDetails({
			name: null,
			description: null,
			isAttachmentRequired: false,
			maxPaidLeavesPerMonth: null,
			leaveEncashment: false,
		});
		setFormError({
			name: false,
			description: false,
			maxPaidLeavesPerMonth: false,
		});
		setFormErrorMessages({
			name: '',
			description: '',
			maxPaidLeavesPerMonth: '',
		});
		setIsLoading(false);
		handleCancel();
	};

	const leaveButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'secondary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: false,
			disabled: isLoading,
			onclick: () => {
				handleModalCancel();
			},
		},
		{
			text: 'Save & next',
			isLoading: isLoading,
			className: 'primary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: true,
			disabled: isLoading,
			onclick: () => {
				handleSubmit();
			},
		},
	];

	const handleChange = (
		value: string | number | null | string[] | boolean,
		name: string,
		required: boolean,
		regex?: RegExp | null
	) => {
		let error = false;
		let errorMessage = '';

		// Handle required string fields
		if (required && typeof value === 'string') {
			if (invalidText(value)) {
				error = true;
				errorMessage = `${name} is required`;
			}
		}

		// Handle required array fields
		if (required && Array.isArray(value) && value.length === 0) {
			error = true;
			errorMessage = `${name} is required`;
		}
		// Handle regex validation
		if (typeof value === 'string' && regex) {
			const _regex = new RegExp(regex);
			if (!_regex.test(value)) {
				error = true;
				errorMessage = `Invalid ${name} format`;
			}
		}

		// Special case: maxPaidLeavesPerMonth - Enhanced validation for decimals
		if (name === 'maxPaidLeavesPerMonth') {
			if (value === null || value === undefined || value === '') {
				error = true;
				errorMessage = 'Leaves Per Month is required';
			} else {
				const numValue = Number(value);
				// Check if it's a valid number
				if (isNaN(numValue)) {
					error = true;
					errorMessage = 'Leaves must be a valid number';
				}
				// Check if it's a decimal (not an integer)
				else if (!Number.isInteger(numValue)) {
					error = true;
					errorMessage =
						'Leaves must be a whole number (no decimals allowed)';
				}
				// Check range and other validations
				else if (numValue <= 0) {
					error = true;
					errorMessage =
						'Max Paid Leaves Per Month must be greater than 0';
				} else if (numValue >= 30) {
					error = true;
					errorMessage =
						'Max Paid Leaves Per Month must be less than 30';
				}
			}
		}
		setFormError((prev: any) => ({
			...prev,
			[name]: error,
		}));

		setFormErrorMessages((prev: any) => ({
			...prev,
			[name]: errorMessage,
		}));

		OnChange(value, name);
	};

	const OnChange = (
		value: string | number | null | string[] | boolean,
		key: string
	) => {
		setAddLeaveDetails((prev: any) => {
			return {
				...prev,
				[key]: value,
			};
		});

		const checkFormError = validateFormData(
			{ [key]: value },
			{ ...formError }
		);
		setFormError(checkFormError);
	};

	const handleSubmit = async () => {
		if (
			addLeaveDetails.maxPaidLeavesPerMonth === null ||
			addLeaveDetails.maxPaidLeavesPerMonth === 0 ||
			Number(addLeaveDetails.maxPaidLeavesPerMonth) >= 30 ||
			!Number.isInteger(Number(addLeaveDetails.maxPaidLeavesPerMonth))
		) {
			setFormError((prev: any) => {
				return {
					...prev,
					maxPaidLeavesPerMonth: true,
				};
			});
			return;
		}

		let checkFormError = validateFormData(
			{ ...addLeaveDetails },
			{ ...formError }
		);

		setFormError(checkFormError);

		if (hasFormError(checkFormError)) {
			return;
		}

		const data = {
			name: addLeaveDetails.name,
			description: addLeaveDetails.description,
			attachmentRequired: addLeaveDetails.isAttachmentRequired,
			maximumLeavePerMonth: addLeaveDetails.maxPaidLeavesPerMonth,
			leaveEncashment: addLeaveDetails.leaveEncashment,
		};

		if (isEdit) {
			try {
				setIsLoading(true);
				const response = await leaveManagementApi.editLeaveManagement(
					editData.id,
					data
				);
				let message =
					response?.data?.message ||
					'Leave Type updated Successfully';
				toastText(message, 'success');
				setEditForConfig(true);
				if (response.data.data) {
					setLeaveManagementId(response?.data?.data?.id);
					setLeaveHistoryId(
						response?.data?.data?.leaveManagementHistoryId
					);
					setLeaveName(addLeaveDetails.name);
					openConfigRuleModal();
				}
			} catch (error: any) {
				let message =
					error?.response?.data?.message ||
					'Something went wrong in editing Leave Type';
				toastText(message, 'error');
			} finally {
				setIsLoading(false);
			}
		} else {
			try {
				setIsLoading(true);

				const response = await leaveManagementApi.addLeaveManagement(
					data
				);

				let message =
					response?.data?.message ||
					'Leave Type updated Successfully';
				toastText(message, 'success');
				setEditForConfig(false);
				if (response.data.data) {
					setLeaveManagementId(response?.data?.data?.id);
					setLeaveName(addLeaveDetails.name);
					handleModalCancel();
					openConfigRuleModal();
				}
			} catch (error: any) {
				let message =
					error?.response?.data?.message ||
					'Error in adding Leave Type';
				toastText(message, 'error');
			} finally {
				setIsLoading(false);
			}
		}
	};

	useEffect(() => {
		if (isEdit) {
			setAddLeaveDetails({
				name: editData.name,
				description: editData.description,
				isAttachmentRequired: editData.isAttachmentRequired,
				maxPaidLeavesPerMonth:
					editData.maxPaidLeavesPerMonth === 0
						? null
						: editData.maxPaidLeavesPerMonth,
				leaveEncashment: editData.leaveEncashment,
			});
			setEditForConfig(true);
		} else {
			setEditForConfig(false);
		}
	}, [isEdit]);

	return (
		<>
			<Modal
				open={open}
				onCancel={handleModalCancel}
				closable={false}
				width={600}
				footer={null}
				destroyOnClose={true}
				maskClosable={false}
				className="leave-type-modal"
			>
				<div className={`${styles['modal']} modal`}>
					<div className={styles['modal-header']}>
						<h4> {isEdit ? 'Edit leave' : 'Add leave'} </h4>
						<div
							className={styles['close-icon']}
							onClick={handleModalCancel}
						>
							<CloseOutlined />
						</div>
					</div>
					<p className={styles['form-container-head-warning']}>
						<b>
							{' '}
							<sup>*</sup>
						</b>{' '}
						Indicated mandatory fields
					</p>
					<Row className={styles['form-container-card']} gutter={50}>
						<Col span={24} className={styles['col']}>
							<InputField
								name="name"
								value={addLeaveDetails.name}
								label="Name"
								required={true}
								helperText={
									formErrorMessages.name ||
									'Leave name required'
								}
								onChange={(value) => {
									handleChange(value, 'name', true);
								}}
								isError={formError.name}
								disabled={editData?.isDefault}
							/>
						</Col>
						<Col span={24} className={styles['col']}>
							<InputNumberField
								name="maxPaidLeavesPerMonth"
								value={addLeaveDetails.maxPaidLeavesPerMonth}
								label="Max Paid Leaves Per Month"
								required={true}
								helperText={
									formErrorMessages.maxPaidLeavesPerMonth ||
									'Max Paid Leave must be a whole number less than 30'
								}
								onChange={(value) => {
									handleChange(
										value,
										'maxPaidLeavesPerMonth',
										true
									);
								}}
								isError={formError.maxPaidLeavesPerMonth}
								disabled={false}
							/>
						</Col>
						<Col span={24}>
							<TextareaFieldInput
								name="description"
								value={addLeaveDetails.description}
								label="Description"
								required={true}
								helperText={
									formErrorMessages.description ||
									'Description required'
								}
								onChange={(value) => {
									handleChange(value, 'description', true);
								}}
								isError={formError.description}
								disabled={false}
							/>
						</Col>
						<Col
							span={12}
							className={styles['col']}
							style={{
								paddingRight: '0px !important',
							}}
						>
							<div className={styles['checkbox-wrapper']}>
								<CheckBox
									checked={
										addLeaveDetails.isAttachmentRequired
									}
									label="Attachment Required"
									onChange={(value) =>
										handleChange(
											value,
											'isAttachmentRequired',
											false
										)
									}
								/>
							</div>
						</Col>
						<Col
							span={12}
							className={styles['col']}
							style={{
								paddingLeft: '0px !important',
							}}
						>
							<div className={styles['checkbox-wrapper']}>
								<CheckBox
									checked={addLeaveDetails.leaveEncashment}
									label="Leave Encashment"
									onChange={(value) =>
										handleChange(
											value,
											'leaveEncashment',
											false
										)
									}
								/>
							</div>
						</Col>
					</Row>

					<div className={styles['buttons']}>
						<Buttons buttons={leaveButtons} />
					</div>
				</div>
			</Modal>
			{configRuleModal && (
				<ConfigRuleModal
					open={configRuleModal}
					leaveManagementId={leaveManagementId}
					fetchLeaveManagementData={fetchLeaveManagementData}
					leaveName={leaveName}
					closeModal={handleModalCancel}
					configLoading={configLoading}
					setConfigLoading={setConfigLoading}
					EditForConfig={EditForConfig}
					leaveHistoryId={leaveHistoryId}
					// handleSubmitForConfiguration={handleSubmitForConfiguration}
				/>
			)}
		</>
	);
};

export default AddLeaveManagementModal;
